import { Logo } from '@/components/logo';
import { Disclosure } from '@/components/seo/disclosure';
import { ThemedText } from '@/components/themed-text';
import { PressableScale } from '@/components/ui/pressable-scale';
import { SEGMENT_PAGES } from '@/content/seo/segments';
import { Ionicons } from '@expo/vector-icons';
import { Href, Link, router } from 'expo-router';
import React, { useState } from 'react';
import { Platform, Pressable, View } from 'react-native';
import { SITE_SLOGAN } from '@/components/seo-head';
import { COMPANY } from '@/content/facts';
import { SiteNavItem, type SiteNavProps } from './site-header';

const isNative = Platform.OS !== 'web';

// Seções da home: na web viram <a href="/#id"> (ver `SiteNavItem`).
const HOME_SECTIONS = [
  { label: 'Funcionalidades', id: 'funcionalidades' },
  { label: 'Planos', id: 'planos' },
].filter((item) => !(Platform.OS === 'ios' && item.id === 'planos'));

// Links reais no rodapé: a web os entrega como <a href>, que buscadores seguem.
// Um nome por destino: o mesmo endereço nunca aparece com dois rótulos.
const PRODUCT_LINKS: { label: string; href: Href }[] = [
  { label: 'Buscar profissionais', href: '/buscar' },
  { label: 'Criar conta grátis', href: '/(auth)/register' },
  { label: 'Guias', href: '/guias' as Href },
  { label: 'Agenda no WhatsApp ou app?', href: '/comparar/agenda-no-whatsapp' as Href },
];

const SUPPORT_LINKS: { label: string; href: Href }[] = [
  { label: 'Sobre a Controlapag', href: '/sobre' as Href },
  { label: 'Central de ajuda', href: '/suporte' as Href },
  { label: 'Contato', href: '/contato' as Href },
  { label: 'Recebimento pelo link', href: '/ajuda/recebimentos' as Href },
  { label: 'Termos de uso', href: '/terms' as Href },
  { label: 'Política de privacidade', href: '/privacidade' as Href },
];

/**
 * Páginas por ramo: o link do rodapé é o que leva o buscador até elas. Ficam
 * recolhidas atrás de "Para o seu ramo", mas sempre no DOM (<a href> no HTML
 * estático) — o buscador segue igual, a pessoa não vê uma lista de oito.
 */
function SegmentLinks({ itemClassName }: { itemClassName: string }) {
  const [open, setOpen] = useState(false);
  return (
    <View>
      <Pressable
        onPress={() => setOpen((v) => !v)}
        accessibilityRole="button"
        accessibilityState={{ expanded: open }}
        className="flex-row items-center gap-1 py-0.5"
      >
        <ThemedText className={itemClassName}>Para o seu ramo</ThemedText>
        <Ionicons className="text-neutral-500" name={open ? 'chevron-up' : 'chevron-down'} size={16} />
      </Pressable>
      <Disclosure open={open}>
        <View className="gap-2 pl-3 pt-2">
          {SEGMENT_PAGES.map((page) => (
            <Link key={page.slug} href={`/para/${page.slug}` as Href} asChild>
              <Pressable className="py-0.5">
                <ThemedText className={itemClassName}>{page.label}</ThemedText>
              </Pressable>
            </Link>
          ))}
        </View>
      </Disclosure>
    </View>
  );
}

/** Rodapé do site público: o mesmo na home e nas páginas de SEO. */
export function SiteFooter({ onNavigate }: SiteNavProps) {
  const goTo = (id: string) => (onNavigate ? onNavigate(id) : router.push('/'));

  return (
  <View
    className={`relative border-t border-neutral-900/5 bg-neutral-50 px-4 ${isNative ? 'py-10' : 'py-16'}`}
  >
    <View className="pointer-events-none absolute -bottom-24 right-[6%] z-0 h-32 w-32 rounded-full bg-primary-50/60 md:h-52 md:w-52" />

    <View className="relative z-10 mx-auto max-w-7xl">
      <View
        className={
          isNative
            ? 'flex flex-col gap-8'
            : 'grid grid-cols-1 gap-12 md:grid-cols-4 lg:gap-16'
        }
      >
        <View className={isNative ? undefined : 'col-span-1 md:col-span-2'}>
          <View className="mb-4 flex flex-row items-center gap-3">
            <Logo size={42} color="tint" />
          </View>
          <ThemedText className="mb-4 max-w-sm text-base leading-relaxed text-neutral-500">
            {SITE_SLOGAN}
          </ThemedText>
          <View className="mb-2 flex flex-row gap-4">
            {[
              { icon: 'logo-instagram', url: COMPANY.instagram, label: 'Instagram' },
              { icon: 'logo-facebook', url: COMPANY.facebook, label: 'Facebook' },
              { icon: 'logo-linkedin', url: COMPANY.linkedin, label: 'LinkedIn' },
            ].map((item) => (
              <Link key={item.icon} href={item.url as any} asChild>
                <PressableScale
                  accessibilityRole="link"
                  accessibilityLabel={`Controlapag no ${item.label}`}
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-50 dark:bg-primary"
                  haptic
                  {...(Platform.OS === 'web'
                    ? { hrefAttrs: { target: '_blank', rel: 'noreferrer' } }
                    : {})}
                >
                  <Ionicons className="text-primary dark:text-white" name={item.icon as any} size={20} />
                </PressableScale>
              </Link>
            ))}
          </View>
        </View>

        {isNative ? (
          <View className="flex-row gap-8">
            <View className="flex-1">
              <ThemedText className="mb-4 font-bold text-lg">Produto</ThemedText>
              <View className="gap-3">
                {HOME_SECTIONS.map((item) => (
                  <Pressable key={item.id} onPress={() => goTo(item.id)} className="py-0.5">
                    <ThemedText className="text-base text-neutral-500">
                      {item.label}
                    </ThemedText>
                  </Pressable>
                ))}
                {PRODUCT_LINKS.map((item) => (
                  <Link key={item.label} href={item.href} asChild>
                    <Pressable className="py-0.5">
                      <ThemedText className="text-base text-neutral-500">
                        {item.label}
                      </ThemedText>
                    </Pressable>
                  </Link>
                ))}
                <SegmentLinks itemClassName="text-base text-neutral-500" />
              </View>
            </View>

            <View className="flex-1">
              <ThemedText className="mb-4 font-bold text-lg">Suporte</ThemedText>
              <View className="gap-3">
                {SUPPORT_LINKS.map((item) => (
                  <Link key={item.label} href={item.href} asChild>
                    <Pressable className="py-0.5">
                      <ThemedText className="text-base text-neutral-500">
                        {item.label}
                      </ThemedText>
                    </Pressable>
                  </Link>
                ))}
              </View>
            </View>
          </View>
        ) : (
          <>
            <View>
              <ThemedText className="mb-6 font-bold text-lg">Produto</ThemedText>
              <View className="space-y-4">
                {HOME_SECTIONS.map((item) => (
                  <SiteNavItem key={item.id} id={item.id} onNavigate={onNavigate}>
                    <ThemedText className="text-base text-neutral-500 transition-colors hover:text-primary">
                      {item.label}
                    </ThemedText>
                  </SiteNavItem>
                ))}
                {PRODUCT_LINKS.map((item) => (
                  <Link key={item.label} href={item.href} asChild>
                    <Pressable>
                      <ThemedText className="text-base text-neutral-500 transition-colors hover:text-primary">
                        {item.label}
                      </ThemedText>
                    </Pressable>
                  </Link>
                ))}
                <SegmentLinks itemClassName="text-base text-neutral-500 transition-colors hover:text-primary" />
              </View>
            </View>

            <View>
              <ThemedText className="mb-6 font-bold text-lg">Suporte</ThemedText>
              <View className="space-y-4">
                {SUPPORT_LINKS.map((item) => (
                  <Link key={item.label} href={item.href} asChild>
                    <Pressable>
                      <ThemedText className="text-base text-neutral-500 transition-colors hover:text-primary">
                        {item.label}
                      </ThemedText>
                    </Pressable>
                  </Link>
                ))}
              </View>
            </View>
          </>
        )}
      </View>

      <View
        className={`flex flex-col items-center justify-between gap-4 border-t border-neutral-900/5 pt-6 text-sm md:flex-row ${isNative ? 'mt-8' : 'mt-16 gap-6 pt-8'}`}
      >
        <ThemedText className={`text-center text-neutral-400 ${isNative ? 'text-xs' : ''}`}>
          © {new Date().getFullYear()} Controlapag. Todos os direitos reservados.
        </ThemedText>
        <View className={`flex flex-row text-neutral-400 ${isNative ? 'gap-4' : 'gap-8'}`}>
          <Link href="/terms" asChild>
            <ThemedText className={`text-neutral-400 ${isNative ? 'text-xs' : ''}`}>
              Termos de uso
            </ThemedText>
          </Link>
          <Link href={'/privacidade' as Href} asChild>
            <ThemedText className={`text-neutral-400 ${isNative ? 'text-xs' : ''}`}>
              Política de privacidade
            </ThemedText>
          </Link>
        </View>
      </View>
    </View>
  </View>
  );
}
